import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Eye, BookOpen, Users, Search, X, ChevronLeft, ChevronRight, School } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { Subject, Class, getSubjects, getClasses, addSubject, addClass, updateSubject, updateClass, deleteSubject, deleteClass, addSubjectToClass, removeSubjectFromClass } from '../utils/subjectsClasses';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';

const AdminManagement: React.FC = () => {
    const [subjects, setSubjects] = useState<Subject[]>([]);
    const [classes, setClasses] = useState<Class[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClass, setSelectedClass] = useState<Class | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
    const [viewingClass, setViewingClass] = useState<Class | null>(null);
    const [showSubjectForm, setShowSubjectForm] = useState(false);
    const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
    const [subjectForm, setSubjectForm] = useState({
        name: ''
    });
    const { showSuccess, showError } = useToast();

    // Class form state
    const [showClassForm, setShowClassForm] = useState(false);
    const [editingClass, setEditingClass] = useState<Class | null>(null);
    const [classForm, setClassForm] = useState({
        grade: '',
        section: '',
        subjects: [] as string[]
    });
    const [savingClass, setSavingClass] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const classesData = await getClasses();
            console.log('Fetched classes:', classesData);
            setClasses(classesData);
        } catch (error) {
            console.error('Error fetching data:', error);
            showError('Error', 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleAddSubjectToClass = (classId: string) => {
        setSelectedClass(classes.find(c => c.id === classId) || null);
        setEditingSubject(null);
        setSubjectForm({ name: '' });
        setShowSubjectForm(true);
    };

    const handleSubjectSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedClass) {
            showError('Error', 'Please select a class first');
            return;
        }

        try {
            if (editingSubject) {
                // For editing, we need to remove old subject and add new one
                await removeSubjectFromClass(selectedClass.id, editingSubject.name);
                await addSubjectToClass(selectedClass.id, subjectForm.name);
                showSuccess('Subject Updated', 'Subject has been successfully updated.');
            } else {
                await addSubjectToClass(selectedClass.id, subjectForm.name);
                showSuccess('Subject Added', 'Subject has been successfully added.');
            }
            setShowSubjectForm(false);
            setEditingSubject(null);
            setSubjectForm({ name: '' });
            fetchData(); // Refresh the classes data
        } catch (error) {
            showError('Error', 'Failed to save subject');
        }
    };

    const handleClassSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSavingClass(true);
        try {
            if (editingClass) {
                await updateClass(editingClass.id, classForm);
                showSuccess('Class Updated', 'Class has been successfully updated.');
            } else {
                await addClass(classForm);
                showSuccess('Class Added', 'Class has been successfully added.');
            }
            setShowClassForm(false);
            setEditingClass(null);
            setClassForm({ grade: '', section: '', subjects: [] });
            fetchData();
        } catch (error) {
            showError('Error', 'Failed to save class');
        } finally {
            setSavingClass(false);
        }
    };

    const handleEditSubject = (subjectName: string) => {
        setEditingSubject({ id: '', name: subjectName, classId: selectedClass?.id || '', createdAt: new Date(), updatedAt: new Date() });
        setSubjectForm({
            name: subjectName
        });
        setShowSubjectForm(true);
    };

    const handleEditClass = (cls: Class) => {
        setEditingClass(cls);
        setClassForm({
            grade: cls.grade,
            section: cls.section,
            subjects: cls.subjects || []
        });
        setShowClassForm(true);
    };

    const handleDeleteSubject = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this subject?')) {
            try {
                await deleteSubject(id);
                showSuccess('Subject Deleted', 'Subject has been successfully deleted.');
            } catch (error) {
                showError('Error', 'Failed to delete subject');
            }
        }
    };


    // Filter and search classes
    const filteredClasses = useMemo(() => {
        return classes.filter(cls =>
            cls.grade.toLowerCase().includes(searchTerm.toLowerCase()) ||
            cls.section.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [classes, searchTerm]);

    // Pagination
    const totalPages = Math.ceil(filteredClasses.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedClasses = filteredClasses.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (value: string) => {
        setItemsPerPage(parseInt(value));
        setCurrentPage(1);
    };

    const ClassCard: React.FC<{ cls: Class }> = ({ cls }) => (
        <Card glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                            <Users className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {cls.grade} - {cls.section}
                            </h3>
                            <p className="text-sm text-gray-600">Class</p>
                        </div>
                    </div>
                    <div className="flex space-x-1">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setViewingClass(cls)}
                        >
                            <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleAddSubjectToClass(cls.id)}
                        >
                            <Plus className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleEditClass(cls)}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Users className="h-4 w-4" />
                        <span>Grade: {cls.grade}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <BookOpen className="h-4 w-4" />
                        <span>Section: {cls.section}</span>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                            ACTIVE
                        </span>
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <BookOpen className="h-3 w-3" />
                        <span>{cls.subjects?.length || 0} subjects</span>
                    </div>
                </div>
            </div>
        </Card>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg">
                        <School className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Class Management</h2>
                        <p className="text-gray-600">Manage classes and their subjects</p>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    <Button
                        onClick={() => {
                            setEditingClass(null);
                            setClassForm({ grade: '', section: '', subjects: [] });
                            setShowClassForm(true);
                        }}
                        className="flex items-center space-x-2"
                    >
                        <Plus className="h-4 w-4" />
                        <span>Add Class</span>
                    </Button>
                    <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">View:</span>
                        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                            <button
                                onClick={() => setViewMode('table')}
                                className={`px-3 py-1 text-sm font-medium transition-colors ${viewMode === 'table'
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                Table
                            </button>
                            <button
                                onClick={() => setViewMode('cards')}
                                className={`px-3 py-1 text-sm font-medium transition-colors ${viewMode === 'cards'
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                Cards
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Input
                                label="Search Classes"
                                placeholder="Search by grade or section..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                icon={<Search className="h-4 w-4" />}
                            />
                        </div>
                    </div>
                </div>
            </Card>

            {/* Results Count */}
            <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredClasses.length)} of {filteredClasses.length} classes
                </p>
                <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Items per page:</span>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                    </select>
                </div>
            </div>

            {/* Classes List */}
            {viewMode === 'table' ? (
                // Table View
                <Card glassy={true} className="islamic-pattern overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Class
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Grade
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Section
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Subjects
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedClasses.map((cls) => (
                                    <tr key={cls.id} className="hover:bg-gray-50 transition-colors duration-200">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                                                    <Users className="h-4 w-4 text-white" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {cls.grade} - {cls.section}
                                                    </div>
                                                    <div className="text-sm text-gray-500">Class</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {cls.grade}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {cls.section}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-1 text-xs text-gray-500">
                                                <BookOpen className="h-3 w-3" />
                                                <span>{cls.subjects?.length || 0} subjects</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center space-x-2">
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => setViewingClass(cls)}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => handleAddSubjectToClass(cls.id)}
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => handleEditClass(cls)}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                // Cards View
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginatedClasses.map((cls) => (
                        <ClassCard key={cls.id} cls={cls} />
                    ))}
                </div>
            )}

            {filteredClasses.length === 0 && (
                <Card glassy={true} className="islamic-pattern">
                    <div className="text-center py-12">
                        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No classes found</h3>
                        <p className="text-gray-600">
                            {searchTerm
                                ? 'Try adjusting your search criteria.'
                                : 'No classes have been added yet.'
                            }
                        </p>
                    </div>
                </Card>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <Card glassy={true} className="islamic-pattern">
                    <div className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-700">
                                    Page {currentPage} of {totalPages}
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>

                                {/* Page Numbers */}
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum: number;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                        <Button
                                            key={pageNum}
                                            variant={currentPage === pageNum ? "primary" : "secondary"}
                                            size="sm"
                                            onClick={() => handlePageChange(pageNum)}
                                        >
                                            {pageNum}
                                        </Button>
                                    );
                                })}

                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Subject Form Modal */}
            {showSubjectForm && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowSubjectForm(false)}
                    title={editingSubject ? 'Edit Subject' : 'Add Subject'}
                    size="md"
                >
                    <form onSubmit={handleSubjectSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Subject Name</label>
                            <input
                                type="text"
                                value={subjectForm.name}
                                onChange={(e) => setSubjectForm(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                placeholder="e.g., Mathematics, Science, English"
                                required
                            />
                        </div>
                        {selectedClass && (
                            <div className="bg-blue-50 p-3 rounded-lg">
                                <p className="text-sm text-blue-700">
                                    <strong>Class:</strong> {selectedClass.grade} - {selectedClass.section}
                                </p>
                            </div>
                        )}
                        <div className="flex justify-end space-x-3 pt-4">
                            <Button
                                variant="secondary"
                                onClick={() => setShowSubjectForm(false)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit">
                                {editingSubject ? 'Update' : 'Add'} Subject
                            </Button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Class Form Modal */}
            {showClassForm && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowClassForm(false)}
                    title={editingClass ? 'Edit Class' : 'Add Class'}
                    size="md"
                >
                    <form onSubmit={handleClassSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                                <input
                                    type="text"
                                    value={classForm.grade}
                                    onChange={(e) => setClassForm(prev => ({ ...prev, grade: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    placeholder="e.g., 10, 11, 12"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Section</label>
                                <input
                                    type="text"
                                    value={classForm.section}
                                    onChange={(e) => setClassForm(prev => ({ ...prev, section: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    placeholder="e.g., A, B, C"
                                    required
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 pt-4">
                            <Button
                                variant="secondary"
                                onClick={() => setShowClassForm(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={savingClass}
                                className="flex items-center"
                            >
                                {savingClass && (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                )}
                                {savingClass ? 'Saving...' : `${editingClass ? 'Update' : 'Add'} Class`}
                            </Button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Class Details Modal */}
            {viewingClass && (
                <Modal
                    isOpen={true}
                    onClose={() => setViewingClass(null)}
                    title={`Class Details - ${viewingClass.grade} - ${viewingClass.section}`}
                    size="md"
                >
                    <div className="space-y-6">
                        {/* Basic Information */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <Users className="h-6 w-6 mr-3 text-emerald-600" />
                                Class Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingClass.grade}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Section</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingClass.section}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Created</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingClass.createdAt.toLocaleDateString()}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Updated</label>
                                    <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                                        {viewingClass.updatedAt.toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Subjects */}
                        <Card className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <BookOpen className="h-6 w-6 mr-3 text-emerald-600" />
                                Subjects ({viewingClass.subjects?.length || 0})
                            </h3>
                            {viewingClass.subjects && viewingClass.subjects.length > 0 ? (
                                <div className="space-y-3">
                                    {viewingClass.subjects.map((subject, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center space-x-3">
                                                <BookOpen className="h-4 w-4 text-emerald-600" />
                                                <span className="text-gray-900">{subject}</span>
                                            </div>
                                            <div className="flex space-x-2">
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                        setViewingClass(null);
                                                        handleEditSubject(subject);
                                                    }}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={async () => {
                                                        try {
                                                            await removeSubjectFromClass(viewingClass.id, subject);
                                                            showSuccess('Subject Removed', 'Subject has been removed from class.');
                                                            fetchData();
                                                        } catch (error) {
                                                            showError('Error', 'Failed to remove subject');
                                                        }
                                                    }}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <h4 className="text-lg font-medium text-gray-900 mb-2">No subjects yet</h4>
                                    <p className="text-gray-600 mb-4">This class doesn't have any subjects assigned yet.</p>
                                </div>
                            )}
                            <div className="mt-4">
                                <Button
                                    onClick={() => {
                                        setViewingClass(null);
                                        handleAddSubjectToClass(viewingClass.id);
                                    }}
                                    className="flex items-center space-x-2"
                                >
                                    <Plus className="h-4 w-4" />
                                    <span>Add Subject</span>
                                </Button>
                            </div>
                        </Card>

                        {/* Modal Actions */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                            <Button
                                variant="secondary"
                                onClick={() => setViewingClass(null)}
                            >
                                Close
                            </Button>
                            <Button
                                onClick={() => {
                                    setViewingClass(null);
                                    handleEditClass(viewingClass);
                                }}
                            >
                                Edit Class
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AdminManagement;